import { useEffect, useState } from "react";
import { LibraryBig } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { BrandButton } from "@/components/kit/BrandButton";
import { AccessDates } from "@/components/kit/AccessDates";
import { Accent } from "@/components/kit/Accent";
import { EmptyState } from "@/components/kit/EmptyState";
import { InstallAppCard } from "@/components/kit/InstallAppCard";
import { PlanCard, PlanRow } from "@/components/kit/PlanRow";
import { LibrarySkeleton } from "@/components/kit/Skeleton";
import { useAuth } from "@/lib/auth";
import { api, isPreview, routes } from "@/lib/ppcp";
import { KIT, PAGES } from "@/constants/testIds";

/** Resume-first: whatever you're in the middle of comes first, then not-started, then finished. */
const resumeFirst = (a, b) => {
  const rank = (p) => (p.progress?.started && !p.progress?.finished ? 0 : p.progress?.finished ? 2 : 1);
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  return (b.progress?.updated_at ?? "").localeCompare(a.progress?.updated_at ?? "");
};

/** The card is the plan itself: enter or resume it. The day you are on is stated, not actioned. */
const label = (program) => {
  if (program.status === "shell") return "Content pending";
  const p = program.progress;
  if (p?.finished) return "Start Plan Again";
  if (p?.started) return "Continue Plan";
  return "Start Plan";
};

const position = (program) => {
  const p = program.progress;
  if (!p?.total_days) return null;
  if (p.finished) return `All ${p.total_days} days complete`;
  if (p.started) return `Day ${p.resume_day} of ${p.total_days}`;
  return "Not started";
};

/** The two styles of the one Healthy plan, grouped under a single card. */
const HEALTHY_STYLES = {
  "healthy-eating": "Light Plate",
  "healthy-flavour": "Flavour Plate",
};
const STYLE_ORDER = ["healthy-eating", "healthy-flavour"];

export default function Library() {
  const navigate = useNavigate();
  const { user, upgradeUrl, canPreview } = useAuth();
  const [programs, setPrograms] = useState(null);
  // Preview chrome needs the operator capability, not just the URL flag.
  const preview = isPreview() && canPreview;

  useEffect(() => {
    api
      .get("/programs")
      .then((res) => setPrograms([...res.data].sort(resumeFirst)))
      .catch(() => setPrograms([]));
  }, []);

  const firstName = user?.name?.split(" ")[0];
  const inProgress = programs?.find((p) => p.progress?.started && !p.progress?.finished);
  const healthy = (programs ?? [])
    .filter((p) => HEALTHY_STYLES[p.slug])
    .sort((a, b) => STYLE_ORDER.indexOf(a.slug) - STYLE_ORDER.indexOf(b.slug));
  const others = (programs ?? []).filter((p) => !HEALTHY_STYLES[p.slug]);

  return (
    <AppShell
      title={preview ? "All programs" : "My Plans"}
      subtitle={user?.email}
      showBack={false}
      crumbs={[{ label: preview ? "All programs" : "My Plans" }]}
    >
      <div data-testid={PAGES.library} className="contents">
        <header className="space-y-3 pb-1 pt-2">
          <h1 className="font-display text-3xl font-semibold leading-tight text-brand-teal sm:text-4xl">
            <Accent>
              {preview
                ? "Every program, published or not"
                : firstName
                  ? `Welcome back, ${firstName}`
                  : "Your meal plans"}
            </Accent>
          </h1>
          <p className="max-w-xl text-[15px] leading-relaxed text-muted-foreground">
            {preview
              ? "Review content exactly as a customer will see it. Nothing here is visible to customers."
              : inProgress
                ? `You're on Day ${inProgress.progress.resume_day} of ${inProgress.progress.total_days} — open your plan to carry on.`
                : "Your purchased meal plans, ready to follow one day at a time."}
          </p>
        </header>

        <InstallAppCard />

        {programs === null ? (
          <LibrarySkeleton />
        ) : programs.length === 0 ? (
          <EmptyState
            icon={LibraryBig}
            title="No programs yet"
            description="Once you purchase a meal plan on Plattershare it will appear here automatically."
          >
            {upgradeUrl && (
              <BrandButton as="a" name="library-browse" href={upgradeUrl} target="_blank" rel="noopener">
                Browse meal plans
              </BrandButton>
            )}
          </EmptyState>
        ) : (
          <div className="grid gap-6">
            {healthy.length > 0 && (
              <article className="ps-card ps-card-pad space-y-5">
                <header className="space-y-1.5">
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-brand-orange">
                    {healthy.length > 1 ? "Two styles, both included" : "Your healthy plan"}
                  </p>
                  <h2 className="font-display text-[22px] font-bold leading-snug text-brand-teal sm:text-[24px]">
                    30-Day Healthy Meal <span className="text-brand-orange">Plan</span>
                  </h2>
                  {healthy.length > 1 && (
                    <p className="text-[14px] leading-relaxed text-muted-foreground">
                      Pick the style that suits your week. Your progress is saved for each one.
                    </p>
                  )}
                </header>

                <div className="divide-y divide-border">
                  {healthy.map((program) => (
                    <div key={program.slug} className="py-4 first:pt-0 last:pb-0">
                      <PlanRow
                        program={program}
                        title={HEALTHY_STYLES[program.slug]}
                        positionLabel={position(program)}
                        percent={program.progress?.percent ?? 0}
                        started={!!program.progress?.started}
                        ctaLabel={program.progress?.started ? "Continue" : "Start"}
                        onOpen={() => navigate(routes.program(program.slug))}
                      />
                      {preview && (
                        <p
                          data-testid={KIT.previewStatus(program.slug)}
                          className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-orangeInk"
                        >
                          {program.status} · v{program.content_version} · {program.days_authored}/
                          {program.duration_days} days authored
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Purchase details are the same for both styles, so they are stated once. */}
                <AccessDates access={healthy[0].access} slug={healthy[0].slug} className="pt-1" />
              </article>
            )}

            {others.map((program) => (
              <PlanCard
                key={program.slug}
                program={program}
                positionLabel={position(program)}
                percent={program.progress?.percent ?? 0}
                ctaLabel={label(program)}
                onOpen={() => navigate(routes.program(program.slug))}
              >
                <AccessDates access={program.access} slug={program.slug} />
                {preview && (
                  <p
                    data-testid={KIT.previewStatus(program.slug)}
                    className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-orangeInk"
                  >
                    {program.status} · v{program.content_version} · {program.days_authored}/
                    {program.duration_days} days authored
                  </p>
                )}
              </PlanCard>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
